import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de réponse
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Gérer les erreurs 403 (Forbidden) - Accès refusé
      if (error.response.status === 403) {
        const errorMessage = error.response.data?.error || 'Accès refusé. Vous devez être administrateur pour accéder à cette ressource.';
        alert(errorMessage);
        // Optionnel: Rediriger vers la page de connexion
        // window.location.href = '/login';
      }
      // Gérer les erreurs 401 (Unauthorized) - Non authentifié
      else if (error.response.status === 401) {
        const errorMessage = error.response.data?.error || 'Vous devez être connecté pour accéder à cette ressource.';
        alert(errorMessage);
        // Supprimer le token invalide
        localStorage.removeItem('authToken');
        // Rediriger vers la page de connexion
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Service pour les produits
export const productsService = {
  getAll: (params = {}) => api.get('/api/products', { params }),
  getById: (id) => api.get(`/api/products/${id}`),
  getBySubcategory: (subcategoryId, params = {}) => api.get(`/api/subcategories/${subcategoryId}/products`, { params }),
  search: (query, params = {}) => api.get('/api/products/search', {
    params: { q: query, ...params }
  }),
  create: (data) => api.post('/api/products', data),
  update: (id, data) => api.put(`/api/products/${id}`, data),
  delete: (id) => api.delete(`/api/products/${id}`),
  uploadImages: (productId, formData) => api.post(`/api/products/${productId}/images`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
};

// Service pour les catégories
export const categoriesService = {
  getAll: () => api.get('/api/categories'),
  getById: (id) => api.get(`/api/categories/${id}`),
  create: (data) => {
    if (data instanceof FormData) {
      return api.post('/api/categories', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.post('/api/categories', data);
  },
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.put(`/api/categories/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.put(`/api/categories/${id}`, data);
  },
  delete: (id) => api.delete(`/api/categories/${id}`),
  updateOrder: (categories) => api.put('/api/categories/order/update', { categories }),

};

// Service pour les sous-catégories
export const subcategoriesService = {
  getAll: () => api.get('/api/subcategories'),
  getById: (id) => api.get(`/api/subcategories/${id}`),
  getByCategory: (categoryId) => api.get(`/api/categories/${categoryId}/subcategories`),
  create: (data) => {
    if (data instanceof FormData) {
      return api.post('/api/subcategories', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.post('/api/subcategories', data);
  },
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.put(`/api/subcategories/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.put(`/api/subcategories/${id}`, data);
  },
  delete: (id) => api.delete(`/api/subcategories/${id}`),
  updateOrder: (subcategories) => api.put('/api/subcategories/order/update', { subcategories }),

};

// Service pour les TVA
export const tvasService = {
  getAll: () => api.get('/api/tvas'),
  getById: (id) => api.get(`/api/tvas/${id}`),
  create: (data) => api.post('/api/tvas', data),
  update: (id, data) => api.put(`/api/tvas/${id}`, data),
  delete: (id) => api.delete(`/api/tvas/${id}`),
};

// Service pour les marques
export const brandsService = {
  getAll: () => api.get('/api/brands'),
  getById: (id) => api.get(`/api/brands/${id}`),
  create: (formData) => api.post('/api/brands', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id, formData) => api.put(`/api/brands/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  delete: (id) => api.delete(`/api/brands/${id}`),
  deleteLogo: (id) => api.delete(`/api/brands/${id}/logo`),
};

// Service pour les familles
export const familiesService = {
  getAll: () => api.get('/api/families'),
  getById: (id) => api.get(`/api/families/${id}`),
  create: (data) => api.post('/api/families', data),
  update: (id, data) => api.put(`/api/families/${id}`, data),
  delete: (id) => api.delete(`/api/families/${id}`),
};

// Service pour les promotions
export const promotionsService = {
  getAll: () => api.get('/api/promotions'),
  getById: (id) => api.get(`/api/promotions/${id}`),
  create: (data) => api.post('/api/promotions', data),
  update: (id, data) => api.put(`/api/promotions/${id}`, data),
  delete: (id) => api.delete(`/api/promotions/${id}`),
  validateCode: (code, userId) => api.post('/api/promotions/validate-code', { code, userId }),
  calculate: (cartItems, userId, promoCode) => api.post('/api/promotions/calculate', { cartItems, userId, promoCode }),
  getStats: (id) => api.get(`/api/promotions/${id}/stats`),
};

// Service pour les bons de réduction utilisateur
export const vouchersService = {
  getUserVouchers: (userId) => api.get(`/api/users/${userId}/vouchers`),
  getById: (id) => api.get(`/api/vouchers/${id}`),
  create: (data) => api.post('/api/vouchers', data),
  update: (id, data) => api.put(`/api/vouchers/${id}`, data),
  delete: (id) => api.delete(`/api/vouchers/${id}`),
  validateCode: (code, userId) => api.post('/api/vouchers/validate-code', { code, userId }),
  markAsUsed: (id, orderId) => api.post(`/api/vouchers/${id}/mark-used`, { orderId }),
  getStats: (userId) => api.get(`/api/users/${userId}/vouchers/stats`),
};

// Service pour les grades et niveaux
export const gradesService = {
  getAll: () => api.get('/api/grades'),
  getById: (id) => api.get(`/api/grades/${id}`),
  create: (data) => api.post('/api/grades', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id, data) => api.put(`/api/grades/${id}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  delete: (id) => api.delete(`/api/grades/${id}`),
  addBenefit: (gradeId, data) => api.post(`/api/grades/${gradeId}/benefits`, data),
  updateBenefit: (gradeId, benefitId, data) => api.put(`/api/grades/${gradeId}/benefits/${benefitId}`, data),
  deleteBenefit: (gradeId, benefitId) => api.delete(`/api/grades/${gradeId}/benefits/${benefitId}`),
  getUserBenefits: (userId) => api.get(`/api/users/${userId}/benefits`),
};

// Service d'authentification
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

// Service pour les configurations XP
export const xpConfigurationsService = {
  getAll: () => api.get('/api/admin/xp-configurations'),
  getActive: () => api.get('/api/xp-configurations/active'),
  getById: (id) => api.get(`/api/admin/xp-configurations/${id}`),
  getByActionType: (actionType) => api.get(`/api/admin/xp-configurations/action-type/${actionType}`),
  create: (data) => api.post('/api/admin/xp-configurations', data),
  update: (id, data) => api.put(`/api/admin/xp-configurations/${id}`, data),
  toggle: (id) => api.patch(`/api/admin/xp-configurations/${id}/toggle`),
  delete: (id) => api.delete(`/api/admin/xp-configurations/${id}`),
  calculateXp: (actionType, amount) => api.post('/api/xp-configurations/calculate', { actionType, amount }),
};

// Service pour les unités de stock
export const stockUnitsService = {
  list: (params = {}) => api.get('/api/admin/stock-units', { params }),
  summary: (params = {}) => api.get('/api/admin/stock-units/summary', { params }),
  create: (data) => api.post('/api/admin/stock-units', data),
  bulkCreate: (units) => api.post('/api/admin/stock-units/bulk', { units }),
  update: (id, data) => api.put(`/api/admin/stock-units/${id}`, data),
  delete: (id) => api.delete(`/api/admin/stock-units/${id}`),
};

// Service statistiques admin
export const statsService = {
  getGlobalStats: () => api.get('/api/admin/stats'),
};

export default api;
